// Pure, framework-free helpers for saving a project version, extracted out of
// pages/index.vue's saveProjectVersion()/#body template so the logic behind
// PIPE-565 (issues 2, 4 and 5) can be unit-tested without booting Nuxt/Vue.

import { displayValue } from './raciCsvExport.js';

/**
 * Whether a task's section cell has an actual value to display, in the
 * current language. Mirrors the condition PrimeVue's #body template uses to
 * decide between showing the value and showing the "to complete" placeholder.
 *
 * Before the PIPE-565 fix, the placeholder branch was only ever rendered for
 * sectionIndex 0 (the first/frozen column). Every other column of a new or
 * still-empty row rendered NO element at all when the cell had no value yet,
 * so there was nothing in the DOM to click into edit mode (issue 4).
 */
export function hasVisibleSectionValue(task, sectionIndex, currentViewLanguage = 'NL') {
    const section = task && task.sections && task.sections[sectionIndex];
    if (!section || !section.pivot || !section.pivot.value) {
        return false;
    }
    return Boolean(displayValue(section.pivot.value, currentViewLanguage));
}

/**
 * Compute the version (and its tasks) that must actually be sent to the API
 * for a save, plus a rollback snapshot to restore if the request fails.
 *
 * When bumpVersion is false (auto-save/beforeUnmount), the version is
 * returned unchanged: the request updates the current version in place.
 *
 * When bumpVersion is true (the explicit "Opslaan"/"new version" actions),
 * the version id and every task id are nulled out so the request creates a
 * brand new version row, and the version number is incremented - but only
 * when a version already existed. A project's very first version is already
 * seeded with number 1 (see loadVersion()) and must land on "1", not jump
 * straight to "2" (PIPE-565 issue 2: "a save creates the next version, the
 * tag advances").
 */
export function planVersionSave(version, { bumpVersion = false } = {}) {
    if (!version) {
        return { version, rollback: null };
    }

    const rollback = {
        id: version.id,
        number: version.number,
        taskIds: (version.tasks || []).map((task) => task.id),
    };

    if (!bumpVersion) {
        return { version, rollback };
    }

    if (version.id) {
        version.number = (version.number || 0) + 1;
    }
    version.id = null;
    (version.tasks || []).forEach((task) => {
        task.id = null;
    });

    return { version, rollback };
}

/**
 * Restore a version's id/number/task ids from a planVersionSave() rollback
 * snapshot after a failed save, so a failed "bump" doesn't leave the tag
 * looking like it advanced when nothing was actually created server-side,
 * and so the unsaved row/edits are never dropped (PIPE-565 issue 5).
 */
export function rollbackVersionSave(version, rollback) {
    if (!version || !rollback) {
        return version;
    }
    version.id = rollback.id;
    version.number = rollback.number;
    (version.tasks || []).forEach((task, index) => {
        if (index < rollback.taskIds.length) {
            task.id = rollback.taskIds[index];
        }
    });
    return version;
}
